// ======================================================================
// File Name       : UserAction
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import { decamelizeKeys, camelizeKeys } from 'humps';
import _ from 'lodash';
import moment from 'moment';
import * as actionTypes from '../constants/actionTypes/user';
import { commonCallApi, callPythonApi } from '../utils/ApiUtils';
import { SELECT_TARGET_DATA, MODE, DORMANT_STATE, DEFAULT_DATE_FORMAT_SEARCH, DORMANT_STATE_CHANGE_FLAG } from '../constants/index';
import * as userRequests from './httpRequests/user';
import * as groupRequests from './httpRequests/group';
import * as courseRequests from './httpRequests/course';
import * as userHistoryStudentRequests from './httpRequests/userHistoryStudent';
import * as userAttributeRequests from './httpRequests/userAttribute';
import { USER_LIST } from '../constants/PathConstants';
import { USER_TYPE, COURSE_COMPLETITION_STATUS_ID, USER_CREATE_TYPE } from '../constants/UserConstants';
import * as GroupAction from './GroupAction';
import * as userAction from './UserAction';
import { USER_ATTRIBUTE_TYPE } from '../constants/UserAttributeConstants';
import * as userActionCreator from './actionCreators/user';
import { DATA_LIMIT_FLAG } from '../constants/GroupConstants';
import { COURSE_GROUP_RELATION_TYPE } from '../constants/CourseConstants';
import { doPromiseInSequence, getDisplayName } from '../utils/index';
import { COURSE_GROUP_RELATION_TYPE_NAME_LIST } from '../constants/SelectBoxData';

export function fetchUser(searchInfo = {}) {
  return async (dispatch) => {
    const apiRes = await userRequests.postUserGetList({
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo,
    });

    dispatch(receiveUserList(apiRes));
  };
}

export function searchUser(searchInfo = {}, userSecernIdList) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        userType: isNaN(searchInfo.userType) ? null : parseInt(searchInfo.userType, 10),
        managementAuthorityList: searchInfo.managementAuthorityList,
        userName: searchInfo.userName,
        loginId: searchInfo.loginId,
        dormantState: searchInfo.dormantState ? null : DORMANT_STATE.ACTIVE,
        userSecernIdList,
        groupList: searchInfo.selectedGroup ? [searchInfo.selectedGroup.groupSecernId] : null, // #6098
        startPosition: searchInfo.startPosition,
        returnMaxNum: searchInfo.returnMaxNum,
      },
    };
    if (searchInfo.userSortType != null || searchInfo.userSortType != undefined) {
      param.searchInfo.userSortType = searchInfo.userSortType;
    }

    if (searchInfo.ascendingDescendingSort) {
      param.searchInfo.ascendingDescendingSort = searchInfo.ascendingDescendingSort;
    }

    return commonCallApi({
      path: 'user/GetList',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      dispatch(receiveUserList(camelizeJson));
      const { userList } = camelizeJson;
      // get user group relation
      const userSecernIds = [];
      if (userList && userList.length) {
        userList.forEach((userInfo) => {
          const { userSecernId } = userInfo;
          userSecernIds.push(userSecernId);
        });
      }
      dispatch(getGroupList(userSecernIds));
      dispatch(getAuthorityList());
    });
  };
}

export function getUserRelationList(dataToSearch = {}) {
  return dispatch => commonCallApi({
    path: 'user/GetList',
    data: dataToSearch,
    institutionId: 1,
  }).then(camelizeJson => dispatch(receiveUserRelationList(camelizeJson)));
}

export function exportUserCSV(searchInfo, userSecernIdList) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.CSV,
      searchInfo: {
        userType: searchInfo.userType,
        managementAuthorityList: searchInfo.managementAuthorityList,
        userName: searchInfo.userName,
        loginId: searchInfo.loginId,
        dormantState: searchInfo.dormantState ? null : DORMANT_STATE.ACTIVE,
        userSecernIdList,
        groupList: searchInfo.selectedGroup ? [searchInfo.selectedGroup.groupSecernId] : null, // #6098
        startPosition: null,
        returnMaxNum: null,
      },
    };
    return commonCallApi({
      path: 'user/GetList',
      data: param,
      institutionId: 1,
    });
  };
}

export function getChildUserList(userSecernId) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: { parentSecernId: userSecernId },
    };
    commonCallApi({
      path: 'user/GetList',
      data: param,
      institutionId: 1,
    }).then(camelizeJson => dispatch(receiveChildUserList(camelizeJson)));
  };
}

export function getAttributeList(searchInfo = {}) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        userAttributeType: 4,
        userAttributeKeyNum: searchInfo.userAttributeKeyNum,
        userAttributeSearchText: searchInfo.userAttributeSearchText,
      },
    };
    return commonCallApi({
      path: 'userAttribute/GetList',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      const userSecernIdList = [];
      for (const userAttribute of camelizeJson.userAttributeList) {
        userSecernIdList.push(userAttribute.secernId);
      }
      if (userSecernIdList.length) {
        dispatch(searchUser(searchInfo, userSecernIdList));
      } else {
        dispatch({
          type: actionTypes.USER_RECEIVE_USER_LIST,
          userList: [],
          recordNum: 0,
        });
      }
    });
  };
}

export function getGroupList(userSecernIdList, isGetAll) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      dataLimitFlag: DATA_LIMIT_FLAG.LIMIT,
    };
    if (isGetAll) {
      delete param.dataLimitFlag;
    }
    return commonCallApi({
      path: 'group/GetStructure',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      dispatch(receiveGroupList(camelizeJson));
      if (userSecernIdList && userSecernIdList.length) {
        dispatch(getGroupRelationList(userSecernIdList));
      }
      return camelizeJson;
    });
  };
}

export function getUserList(userSecernId) {
  return async (dispatch) => {
    const param = {
      searchInfo: { userSecernIdList: [userSecernId] },
    };
    const result = await userRequests.postUserGetList(param);
    return dispatch(userActionCreator.getAuthorityUserLoging(result));
  };
}

export function getAuthorityList() {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
    };
    commonCallApi({
      path: 'userAuthority/GetList',
      data: param,
      institutionId: 1,
    }).then(camelizeJson => dispatch(receiveAuthorityList(camelizeJson)));
  };
}

export function getAttributeListForUser(userSecernId) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        userAttributeType: 4,
        secernId: userSecernId,
      },
    };
    commonCallApi({
      path: 'userAttribute/GetList',
      data: param,
      institutionId: 1,
    }).then(camelizeJson => dispatch(receiveAttributeList(camelizeJson)));
  };
}

export function getGroupRelationList(userSecernIdList) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        userList: userSecernIdList,
      },
    };
    commonCallApi({
      path: 'userGroupRelation/GetList',
      data: param,
      institutionId: 1,
    }).then(camelizeJson => dispatch(receiveGroupRelationList(camelizeJson)));
  };
}

export function getUserCourseRelationList(userSecernId, isGroupCourse) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        userList: [userSecernId],
      },
    };
    const decamelDataToSearch = decamelizeKeys(param);
    return commonCallApi({
      path: 'userCourseRelation/GetList',
      data: decamelDataToSearch,
      institutionId: 1,
    }).then(({ courseRelationList }) => {

      dispatch(receiveUserCourseRelationList(courseRelationList));
      if (!isGroupCourse) {
        dispatch(getGroupCourseRelation(courseRelationList, userSecernId));
      }
    });
  };
}

export function userEdit(param) {
  return dispatch =>
    commonCallApi({
      path: 'user/Edit',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      dispatch({
        type: actionTypes.USER_EDIT,
        userSecernId: camelizeJson.userSecernId,
      });
    });
}

export function registerUpdateUser(state, props) {
  return (dispatch, getState) => {
    const { mode, userDetail, userAttributes } = state;
    const dormantStateChangeDate = userDetail.dormantStateChangeDate && moment(userDetail.dormantStateChangeDate).format(DEFAULT_DATE_FORMAT_SEARCH);
    let dormantStateChangeFlag = DORMANT_STATE_CHANGE_FLAG.DO_NOT_CHANGE;
    if (userDetail.dormantStateChangeFlag && parseInt(userDetail.dormantStateChangeFlag, 10)) {
      if (parseInt(userDetail.dormantState, 10) === DORMANT_STATE.ACTIVE) {
        dormantStateChangeFlag = DORMANT_STATE_CHANGE_FLAG.CHANGE_WHILE_RUNNING;
      } else if (parseInt(userDetail.dormantState, 10) === DORMANT_STATE.INACTIVE) {
        dormantStateChangeFlag = DORMANT_STATE_CHANGE_FLAG.CHANGE_WHILE_PAUSING;
      }
    }

    const param = {
      editType: mode,
      userInfo: {
        loginId: userDetail.loginId,
        userSecernId: userDetail.userSecernId,
        userType: mode === MODE.NEW_MODE ? userDetail.userType : null,
        userName: userDetail.userName,
        password: mode === MODE.NEW_MODE ? userDetail.password : null,
        afterPassword: mode === MODE.EDIT_MODE ? userDetail.password : null,
        mailAddress: userDetail.mailAddress,
        dormantState: !isNaN(userDetail.userType) &&
                      parseInt(userDetail.userType, 10) === USER_TYPE.STUDENT &&
                      !isNaN(userDetail.dormantState) ?
                      parseInt(userDetail.dormantState, 10) : null,
        dormantStateChangeFlag: !isNaN(userDetail.userType) &&
                                parseInt(userDetail.userType, 10) === USER_TYPE.STUDENT &&
                                !isNaN(userDetail.dormantStateChangeFlag) ?
                                dormantStateChangeFlag : null,
        dormantStateChangeDate: !isNaN(userDetail.userType) &&
                                parseInt(userDetail.userType, 10) === USER_TYPE.STUDENT &&
                                !isNaN(parseInt(dormantStateChangeDate, 10)) ?
                                dormantStateChangeDate : null,
        passwordChangeFlag: userDetail.passwordChangeFlag,
        loginIdPasswordSendFlag: userDetail.loginIdPasswordSendFlag,
        managementAuthoritySecernId: userDetail.managementAuthoritySecernId,
        lockState: mode === (MODE.NEW_MODE || userDetail.isUnlock) ? 0 : userDetail.lockState,
      },
    };
    const { childUserList } = state;
    const { oldChildUserList } = getState().user;
    commonCallApi({
      path: 'user/Edit',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      const promiseAttribute = editUserAttribute(userAttributes, camelizeJson.userSecernId, mode);
      const promiseRelations = getGrouptoUpdateGroupRelation(state, camelizeJson.userSecernId);
      const promiseChilds = getChildUserToUpdate(childUserList, oldChildUserList, camelizeJson.userSecernId);

      // #7222 START
      const promises = promiseChilds.concat(promiseAttribute);
      doPromiseInSequence(promiseRelations).then(() => {
        Promise.all(promises).catch(() => { }).then((responseData) => {
          props.history.push(USER_LIST);
        });
      });
      // #7222 END
    });
  };
}

function getGrouptoUpdateGroupRelation(props, userSecernId) {
  const promiseGroup = [];
  const { selectedGroupList, oldSelectedGroupList } = props;
  // Find delete group relation list
  const deleteRelationList = [];
  oldSelectedGroupList && oldSelectedGroupList.forEach((oldGroup) => {
    let isDeleted = true;
    selectedGroupList && selectedGroupList.forEach((selectedGroup) => {
      if (selectedGroup.groupSecernId === oldGroup.groupSecernId) {
        isDeleted = false;
      }
    }, this);
    if (isDeleted) {
      deleteRelationList.push(oldGroup);
    }
  }, this);
  if (deleteRelationList && deleteRelationList.length) {
    for (const deleteRelation of deleteRelationList) {
      if (deleteRelation.groupSecernId) {
        promiseGroup.push(editUserGroupRelation(MODE.DELETE_MODE,
        deleteRelation.groupSecernId, userSecernId));
      }
    }
  }

  // Find add group relation list
  const addRealtionList = [];
  selectedGroupList && selectedGroupList.forEach((selectedGroup) => {
    let isAdd = true;
    oldSelectedGroupList && oldSelectedGroupList.forEach((oldGroup) => {
      if (selectedGroup.groupSecernId === oldGroup.groupSecernId) {
        isAdd = false;
      }
    }, this);
    if (isAdd) {
      addRealtionList.push(selectedGroup);
    }
  }, this);
  if (addRealtionList && addRealtionList.length) {
    for (const addRelation of addRealtionList) {
      if (addRelation.groupSecernId) {
        promiseGroup.push(editUserGroupRelation(
        MODE.NEW_MODE,
        addRelation.groupSecernId,
        userSecernId));
      }
    }
  }
  return promiseGroup;
}

function getChildUserToUpdate(childUserList, oldChildUserList, userSecernId) {
  const promiseChild = [];
  // Find delete child user list
  const deleteChildList = oldChildUserList.filter(
    obj => !childUserList.some(obj2 => obj.userSecernId === obj2.userSecernId),
  );
  if (deleteChildList && deleteChildList.length) {
    for (const deleteChild of deleteChildList) {
      if (deleteChild) {
        promiseChild.push(editChildUser(MODE.DELETE_MODE, userSecernId, deleteChild));
      }
    }
  }

  // Find add child user list
  const addChildList = childUserList.filter(
    obj => !oldChildUserList.some(obj2 => obj.userSecernId === obj2.userSecernId),
  );
  if (addChildList && addChildList.length) {
    for (const addChild of addChildList) {
      if (addChild) {
        promiseChild.push(editChildUser(MODE.EDIT_MODE, userSecernId, addChild));
      }
    }
  }

  return promiseChild;
}

function editUserAttribute(userAttributes, userSecernId, mode) {
  let userAttributeList = [];
  if (userAttributes) {
    for (const attr of userAttributes.allKeyNums) {
      if (userAttributes.byKeyNum[attr] && userAttributes.byKeyNum[attr].userAttribute !== '') {
        userAttributeList.push(userAttributes.byKeyNum[attr]);
      }
    }
  }
  if (mode === MODE.NEW_MODE && (!userAttributeList || !userAttributeList.length)) {
    return null;
  }
  if (!userAttributeList || userAttributeList.length === 0) {
    userAttributeList = null;
  }
  const param = {
    userAttributeType: 4,
    secernId: userSecernId,
    userAttributeList,
  };
  return commonCallApi({
    path: 'userAttribute/Edit',
    data: param,
    institutionId: 1,
  }).then((camelizeJson) => {});
}

function editUserGroupRelation(type, groupSecernId, userSecernId) {
  // #7222 START
  return async (dispatch, getState) => {
    const param = {
      editType: type,
      groupInfo: {
        groupSecernId,
        userList: [userSecernId],
      },
    };

    await commonCallApi({
      path: 'groupUserRelation/Edit',
      data: param,
      institutionId: 1,
    });
  };
  // #7222 END
}

function editChildUser(type, userSercernId, chilUser) {
  const param = {
    editType: MODE.EDIT_MODE,
    userInfo: {
      userSecernId: chilUser.userSecernId,
      parentSecernId: type === MODE.EDIT_MODE ? userSercernId : '',
    },
  };

  return commonCallApi({
    path: 'user/Edit',
    data: param,
    institutionId: 1,
  })
    .then(response => response.json())
    .then((json) => {});
}

function receiveUserList(userSearchResult) {
  return {
    type: actionTypes.USER_RECEIVE_USER_LIST,
    userList: userSearchResult.userList,
    recordNum: userSearchResult.recordNum,
  };
}

function receiveUserCourseRelationList(courseRelationList) {
  return {
    type: actionTypes.USER_COURSE_RELATION_LIST,
    userCourseRelationList: courseRelationList,
  };
}

function receiveGroupList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_GROUP_LIST,
    groupList: responseData.groupList,
  };
}

function receiveAuthorityList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_AUTHORITY_LIST,
    managementAuthorityList: responseData.managementAuthorityList,
  };
}

function receiveAttributeList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_ATTRIBUTE_LIST,
    userAttributeList: responseData.userAttributeList,
  };
}

function receiveGroupRelationList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_GROUP_RELATION_LIST,
    groupRelationList: responseData.groupRelationList,
  };
}

function receiveCourseList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_COURSE_LIST,
    courseList: responseData.courseList,
  };
}

function receiveChildUserList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_CHILD_USER_LIST,
    childUserList: responseData.userList,
  };
}

function receiveUserRelationList(responseData) {
  return {
    type: actionTypes.USER_RECEIVE_RELATION_LIST,
    userRelationInfo: responseData,
  };
}

export function searchUserCustomParams(params) {
  const param = {
    selectTargetData: SELECT_TARGET_DATA.DATA,
    searchInfo: {
      ...params,
      ascendingDescendingSort: 0,
      startPosition: 0,
      returnMaxNum: 1000,
    },
  };
  return commonCallApi({
    path: 'user/GetList',
    data: param,
    institutionId: 1,
  }).then(({ userList }) => {
    if (!userList) {
      return [];
    }

    const listLength = userList.length;
    const idList = [];
    for (let i = 0; i < listLength; i++) {
      idList.push(userList[i].userSecernId);
    }
    return idList;
  });
}

export function searchUserName(params) {
  return (dispatch) => {
    const param = {
      selectTargetData: SELECT_TARGET_DATA.DATA,
      searchInfo: {
        ...params,
        ascendingDescendingSort: 0,
        startPosition: 0,
        returnMaxNum: 1000,
      },
    };
    commonCallApi({
      path: 'user/GetList',
      data: param,
      institutionId: 1,
    }).then((camelizeJson) => {
      dispatch(receiveUserList(camelizeJson));
    });
  };
}

export function editChildUserList(childUserList) {
  return {
    type: actionTypes.EDIT_CHILD_USER_LIST,
    childUserList,
  };
}

export function searchUserOnDialog({ paramSearchInfo, paramSearchAttribute }, withGroup, isLiveDetail) {
  return async (dispatch, getState) => {
    let userSearchResult = { recordNum: 0, userList: [] };
    let groupRelationList = [];
    let userSecernIds = [];
    let groupInfo = getState().user.groupInfoAll;
    const shouldSearchGroup =
      !groupInfo || !groupInfo.bySecernId || !Object.keys(groupInfo.bySecernId).length;
    if (shouldSearchGroup || isLiveDetail) {
      await dispatch(userAction.getGroupListAll()).then(() => {
        const stateUser = getState().user.groupInfoAll;
        if (stateUser) {
          groupInfo = stateUser;
        }
      });
    }
    if (paramSearchAttribute && paramSearchAttribute.userAttributeSearchText) {
      const { userAttributeList } = await userAttributeRequests.postUserAttributeGetList({
        searchInfo: {
          userAttributeType: USER_ATTRIBUTE_TYPE.USER,
          userAttributeKeyNum: paramSearchAttribute.userAttributeKeyNum,
          userAttributeSearchText: paramSearchAttribute.userAttributeSearchText,
        },
      });
      if (!userAttributeList || !userAttributeList.length) {
        return dispatch(
          userActionCreator.storeUserList(userSearchResult, groupRelationList, groupInfo),
        );
      }
      userSecernIds = userAttributeList.reduce((acc, cur) => {
        if (cur.secernId) {
          acc.push(cur.secernId);
        }
        return acc;
      }, []);
    }

    const { authedUser: { userInfo } } = getState().authed;
    const { storedUserList } = getState().user;
    if (
      userInfo &&
      userInfo.userSecernId &&
      storedUserList[userInfo.userSercernId] &&
      storedUserList[userInfo.userSercernId].shouldSearchGroup
    ) {
      userSecernIds.push(userInfo.userSercernId);
      delete storedUserList[userInfo.userSercernId];
    }
    if (userSecernIds && userSecernIds.length) {
      paramSearchInfo.userSecernIdList = userSecernIds;
    }
    const searchInfo = paramSearchInfo;
    const userSearchRes = await getUsers(searchInfo);
    if (userSearchRes && userSearchRes.userList && userSearchRes.userList.length) {
      userSearchResult = userSearchRes;
    }
    if (userSearchResult && userSearchResult.userList && userSearchResult.userList.length) {
      const searchGroupRelationInfo = {};
      searchGroupRelationInfo.userList = userSearchResult.userList.reduce((acc, cur) => {
        if (cur.userSecernId) {
          acc.push(cur.userSecernId);
        }
        return acc;
      }, []);

      if (searchGroupRelationInfo.userList && searchGroupRelationInfo.userList.length) {
        const groupRelationListRes = await groupRequests.postUserGroupRelationGetList({
          searchInfo: searchGroupRelationInfo,
        });
        if (groupRelationListRes && groupRelationListRes.groupRelationList) {
          groupRelationList = groupRelationListRes.groupRelationList;
        }
      }
    }
    return dispatch(
      userActionCreator.storeUserList(userSearchResult, groupRelationList, groupInfo),
    );
  };
}

function getUsers(searchInfo = {}) {
  return userRequests.postUserGetList({
    selectTargetData: SELECT_TARGET_DATA.DATA,
    searchInfo,
  });
}

export function storeMyUserInfo(userInfo) {
  return async (dispatch, getState) => {
    await dispatch(userActionCreator.storeMyUserInfo(userInfo));
    return {};
  };
}

export function removedDataUserHasGroupAndRole() {
  return {
    type: actionTypes.REMOVED_DATA_USER_HAS_GROUP_AND_ROLE,
  };
}

export function getUserGroupRelation(userSecernId, isGetList) {
  return async (dispatch) => {
    const param = {
      searchInfo: { userList: [userSecernId] },
    };
    const result = await userRequests.postUserGroupRelationGetList(param);
    if (isGetList) {
      return dispatch(userActionCreator.getUserGroupRelationUserLoging(result));
    }
    return result;
  };
}

export function getGroupCourseRelation(userCourseRelationList, userSecernId) {
  return async (dispatch, getState) => {
    const param = {
      searchInfo: { userSecernId },
    };
    let courseList = [];
    if (userCourseRelationList && userCourseRelationList.length) {
      userCourseRelationList.forEach((courseInfo) => {
        courseList.push(courseInfo.courseSecernId);
      });
    }

    const result = await userRequests.getGroupCourseRelation(param);
    if (result.courseRelationList && result.courseRelationList.length) {
      result.courseRelationList.forEach((courseInfo) => {
        const place = courseList.indexOf(courseInfo.courseSecernId);
        if (place < 0) {
          courseList.push(courseInfo.courseSecernId);
        }
      });
    }
    const paramCourse = {
      searchInfo: {
        courseList,
      },
      selectTargetData: SELECT_TARGET_DATA.DATA,
    };
    const groupList = getState().user.allGroup;
    const courseListObj = await courseRequests.postCourseGetList(paramCourse);
    let courseListArray = [];
    let groupListObj = {};
    let courseObj = {};
    if (groupList && groupList.length) {
      groupListObj = groupList.reduce((acc, cur) => {
        if (cur && cur.groupSecernId) {
          acc[cur.groupSecernId] = cur;
        }
        return acc;
      }, {});
    }
    if (courseListObj.courseList && courseListObj.courseList.length) {
      courseObj = courseListObj.courseList.reduce((acc, cur) => {
        if (cur && cur.courseSecernId) {
          acc[cur.courseSecernId] = cur;
        }
        return acc;
      }, {});
    }

    // Step10.0.0 START
    const paramCourseStatus = {
      searchInfo: {
        userList: [userSecernId],
      },
      selectTargetData: SELECT_TARGET_DATA.DATA,
    };
    const { courseStatusList } =
      await userHistoryStudentRequests.postGetCourseByUserList(
        paramCourseStatus
      );
    let courseStatusObj = {};
    if (courseStatusList && courseStatusList.length) {
      courseStatusObj = courseStatusList.reduce(
        (acc, cur) => {
          if (cur && cur.courseSecernId) {
            acc[cur.courseSecernId] = cur;
          }
          return acc;
        },
        {}
      );
    }
    // Step10.0.0 END

    if (userCourseRelationList && userCourseRelationList.length) {
      userCourseRelationList.map((userCourse) => {
        if (courseObj[userCourse.courseSecernId]) {
          userCourse.courseName =
            courseObj[userCourse.courseSecernId].courseName;
          userCourse.groupName = '-';
          userCourse.relationTypeName = '-';
          // Step10.0.0 START
          userCourse.courseStatus = courseStatusObj[userCourse.courseSecernId]
            ? courseStatusObj[userCourse.courseSecernId].courseStatus
            : COURSE_COMPLETITION_STATUS_ID.INCOMPLETE;
          userCourse.courseProgressRatio = courseStatusObj[userCourse.courseSecernId]
            ? courseStatusObj[userCourse.courseSecernId].courseProgressRatio
            : 0;
          // Step10.0.0 END
          courseListArray.push(userCourse);
        }
      });
    }

    if (result && result.courseRelationList) {
      courseListArray = result.courseRelationList.reduce((acc, cur) => {
        if (courseObj[cur.courseSecernId]) {
          cur.courseName = courseObj[cur.courseSecernId].courseName;
          if (groupListObj && groupListObj[cur.groupSecernId]) {
            cur.groupName = groupListObj[cur.groupSecernId].groupName;
          }
          cur.relationTypeName = getDisplayName( COURSE_GROUP_RELATION_TYPE_NAME_LIST, cur.courseGroupRelationType);
          // Step10.0.0 START
          cur.courseStatus = courseStatusObj[cur.courseSecernId]
            ? courseStatusObj[cur.courseSecernId].courseStatus
            : COURSE_COMPLETITION_STATUS_ID.INCOMPLETE;
          cur.courseProgressRatio = courseStatusObj[cur.courseSecernId]
            ? courseStatusObj[cur.courseSecernId].courseProgressRatio
            : 0;
          // Step10.0.0 END
          acc.push(cur);
        }
        return acc;
      }, courseListArray);
    }


    const responseData = {};
    responseData.courseList = courseListArray;
    return dispatch(receiveCourseList(responseData));
  };
}

export function getGroupListAll() {
  return async (dispatch) => {
    const getAll = true;
    const result = await groupRequests.postGroupGetStructure(undefined, getAll);
    return dispatch(receiveAllGroup(result));
  };
}

export function receiveAllGroup(data) {
  return {
    type: actionTypes.RECEIVE_ALL_GROUP,
    data,
  };
}

export function removedUserOnDialog() {
  return {
    type: actionTypes.REMOVED_USER_IN_DIALOG,
  };
}

export function getUserGroupRelationList(searchData = {}) {
  return async (dispatch) => {
    const searchInfo = {
      searchInfo: { ...searchData },
    };
    const result = await userRequests.postUserGroupRelationGetList(searchInfo);
    return result;
  };
}

// Step 9.0.1 START
export function getUserInfoAfterLogin(searchInfo = {}) {
  return (dispatch) => {
    const param = {};
    param.authCd = searchInfo.authCd;
    param.institutionId = searchInfo.institutionId;
    param.selectTargetData = SELECT_TARGET_DATA.DATA;
    param.token = searchInfo.token;
    param.searchInfo = {
      userCreateType: USER_CREATE_TYPE.DEFINED_AND_REPRESENTATIVE_USER, // Step 11.0.0
    };
    searchInfo.userSecernId && (param.searchInfo.userSecernId = searchInfo.userSecernId);
    return commonCallApi({
      path: 'user/GetList',
      data: param,
    });
  };
}
// Step 9.0.1 END

// Step 9.0.1 START
export function sendAccountInfo(userSecernId) {
  return async (dispatch) => {
    const param = {
      userSecernId,
    };
    const result = await userRequests.postSendAccountInfo(param);;
    return result;
  };
}
// Step 9.0.1 END

// Step11.1.0 START
export async function postUserGetManagementServiceList(data) {
  const camelizeJson = await callPythonApi({
    path: 'cms/user/get-management-service-list',
    data: { ...data },
  });

  return camelizeJson;
}
// Step11.1.0 END

// Step 11.0.0.1 START
export function getAllGroupOwnerFlag(searchInfo, institutionId) {
  return async (dispatch) => {
    const result = await postUserGetManagementServiceList(searchInfo);
    const allGroupOwnerFlag = result.serviceList?.find(
      (item) => item.institutionId === institutionId
    )?.allGroupOwnerFlag ?? false;
    return allGroupOwnerFlag;
  };
}
// Step 11.0.0.1 END

// Step 12.0.0 START
export function getManagementUserInfo(param) {
  return async (dispatch) => {
    const result = await postUserGetManagementUserInfo(param);
    dispatch(userActionCreator.getManagementUserInfo(result));
  };
}

export async function postUserGetManagementUserInfo(data) {
  const camelizeJson = await callPythonApi({
    path: 'cms/user/get-management-user-info',
    data: { ...data },
  });

  return camelizeJson;
}
// Step 12.0.0 END
